import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateFormData, nextStep } from '../../store/formSlice';
import {
    Box, Button, VStack, Input, FormControl, FormErrorMessage, Image, Select, FormLabel,
} from '@chakra-ui/react';
import { ProfileImg } from "../../utils/Images";
import StepHeader from './StepHeader';
import { TimePicker, DatePicker, ConfigProvider } from 'antd';
import dayjs from 'dayjs';

// Extracted custom hook for step form logic
function useStepForm(formData) {
    const [step, setStep] = useState(1);
    const [fields, setFields] = useState({
        firstName: formData.firstName || '',
        dateOfBirth: formData.dateOfBirth || '',
        gender: formData.gender || '',
    });
    const [errors, setErrors] = useState({});
    const fileInputRef = useRef();

    // Remove profilePreview cleanup effect

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFields(f => ({ ...f, [name]: value }));
        setErrors(err => ({ ...err, [name]: '' }));
    }, []);

    const validateStepFields = useCallback((step, fields) => {
        let newErrors = {};
        if (step === 1) {
            if (!fields.firstName.trim()) newErrors.firstName = 'First name is required';
            else if (fields.firstName.trim().length < 2) newErrors.firstName = 'First name must be at least 2 characters';
        }
        if (step === 2) {
            if (!fields.dateOfBirth) newErrors.dateOfBirth = 'Please select the date of birth';
            else {
                const dob = new Date(fields.dateOfBirth);
                const today = new Date();
                const age = today.getFullYear() - dob.getFullYear();
                const m = today.getMonth() - dob.getMonth();
                const d = today.getDate() - dob.getDate();
                let ageAdjusted = age;
                if (m < 0 || (m === 0 && d < 0)) ageAdjusted--;
                if (ageAdjusted < 15) newErrors.dateOfBirth = 'User must be at least 15 years old';
            }
        }
        if (step === 3) {
            if (!fields.gender) newErrors.gender = 'Please select a gender';
        }
        return newErrors;
    }, []);

    const validateStep = useCallback(() => {
        const newErrors = validateStepFields(step, fields);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [step, fields, validateStepFields]);

    return {
        step, setStep, fields, setFields, errors, setErrors, fileInputRef, handleChange, validateStep
    };
}

const PersonalizationSteps = React.memo(() => {
    const dispatch = useDispatch();
    const { formData } = useSelector((state) => state.form);
    const {
        step, setStep, fields, setFields, errors, setErrors, fileInputRef, handleChange, validateStep
    } = useStepForm(formData);

    // Step submission
    const handleNext = useCallback(() => {
        if (!validateStep()) return;
        if (step === 1) {
            dispatch(updateFormData({ firstName: fields.firstName.trim() }));
            setStep(2);
        } else if (step === 2) {
            dispatch(updateFormData({ dateOfBirth: fields.dateOfBirth }));
            setStep(3);
        } else if (step === 3) {
            dispatch(updateFormData({ gender: fields.gender }));
            dispatch(nextStep());
        }
    }, [validateStep, step, fields, dispatch, setStep]);

    const handlePrev = useCallback(() => setStep(prev => Math.max(prev - 1, 1)), []);

    // Memoize steps for performance
    const steps = useMemo(() => [
        {
            content: (
                <VStack spacing={6} mb={8} w="full">
                    <FormControl isInvalid={!!errors.firstName} maxW="500px" mx="auto">
                        <Input
                            id="firstName"
                            name="firstName"
                            placeholder="What's your loved one's first name?"
                            value={fields.firstName}
                            onChange={handleChange}
                        />
                        <FormErrorMessage>{errors.firstName}</FormErrorMessage>
                    </FormControl>
                </VStack>
            )
        },
        {
            content: (
                <FormControl isInvalid={!!errors.dateOfBirth} mb={8} maxW="500px" mx="auto">
                    <ConfigProvider theme={{ token: { colorPrimary: '#108084' } }}>
                        <DatePicker
                            id="dateOfBirth"
                            name="dateOfBirth"
                            format="YYYY-MM-DD"
                            value={fields.dateOfBirth ? dayjs(fields.dateOfBirth) : null}
                            onChange={(_, dateString) => handleChange({ target: { name: 'dateOfBirth', value: dateString } })}
                            style={{ width: '100%' }}
                            placeholder="Select date of birth"
                        />
                    </ConfigProvider>
                    <FormErrorMessage>{errors.dateOfBirth}</FormErrorMessage>
                </FormControl>
            )
        },
        {
            content: (
                <FormControl isInvalid={!!errors.gender} mb={8} maxW="500px" mx="auto">
                    <Select
                        id="gender"
                        name="gender"
                        placeholder={`Gender of ${fields.firstName || 'user'}`}
                        value={fields.gender}
                        onChange={handleChange}
                    >
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                    </Select>
                    <FormErrorMessage>{errors.gender}</FormErrorMessage>
                </FormControl>
            )
        }
    ], [fields, errors, handleChange]);

    return (
        <Box maxW="3xl" mx="auto" p={6} textAlign="center">
            <StepHeader
                title="Personalization"
                subtitle={`Few basic questions we need to know`}
            />
            <form onSubmit={e => { e.preventDefault(); handleNext(); }}>
                {steps[step - 1].content}
                <Box display="flex" justifyContent={step !== 1 ? "space-between" : "center"}>
                    {step !== 1 && <Button mt={4} onClick={handlePrev} variant="outline">Previous</Button>}
                    <Button mt={4} type="submit" px="30px">Continue</Button>
                </Box>
            </form>
        </Box>
    );
});

export default PersonalizationSteps; 